import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useLibrarySearch } from "../../../hooks/learningCenter/librarySearch";
import { useTableData } from "../../../components/ReactTable/useTableData";
import SearchPageLayout from "../../../components/FullPageLayout/SearchPageLayout";
import SearchFilters from "../../../components/SearchFilters/SearchFilters";
import Table from "../../../components/ReactTable/Table";
import { useLocation, useHistory } from "react-router-dom";
import {
  imageColumn,
  titleColumn,
  courseActionColumn,
  courseTypeColumn,
  courseProgressColumn,
  policyNumberColumn,
  manualNameColumn,
  iltDateColumn,
  iltLocationColumn
} from "./SearchResultsColumns";
import AddToMyCoursesButton from "./AddToMyCoursesButton";
import { librarySearchFilters } from "../../../constants";
import { makeGetEntities } from "../../../redux/selectors/selectors";
import { useSelector } from "react-redux";
import { librarySearch } from "../../../routes/LearningCenterRoutes";
import usePermissions from "../../../redux/selectors/usePermissions";
import { omit, reduce } from "lodash";
import { isIframed } from "../../../utils/iframe";

const selector = makeGetEntities();

const Search = ({ onAddToCourses, onStartOrResumeClick }) => {
  const { t } = useTranslation();
  const { hasPolicyPartnerLearnerAccess } = usePermissions();

  const availableFilters = useMemo(
    () =>
      !hasPolicyPartnerLearnerAccess || isIframed
        ? omit(librarySearchFilters, ["allPolicies"])
        : librarySearchFilters,
    [hasPolicyPartnerLearnerAccess]
  );

  const defaultFilters = useMemo(
    () =>
      reduce(
        availableFilters,
        (result, value) => {
          result[value] = { checked: true };
          return result;
        },
        {}
      ),
    [availableFilters]
  );

  const useSearchParam = () => {
    return new URLSearchParams(useLocation().search).get("search");
  };
  const [tableFilters, setTableFilters] = useState([]);

  useEffect(() => {
    setTableFilters(defaultFilters);
  }, [defaultFilters]);

  const [searchValue, setSearchValue] = useState(useSearchParam());
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const { searchCourses } = useLibrarySearch();
  const history = useHistory();
  const isAllCoursesFilterSelected = useMemo(
    () => tableFilters[librarySearchFilters.allCourses]?.checked,
    [tableFilters]
  );

  const isAllPoliciesFilterSelected = useMemo(
    () => tableFilters[librarySearchFilters.allPolicies]?.checked,
    [tableFilters]
  );

  const isMyCoursesFilterSelected = useMemo(
    () => tableFilters[librarySearchFilters.myCourses]?.checked,
    [tableFilters]
  );

  const memoizedFilterSelector = filter =>
    filter === librarySearchFilters.allCourses
      ? isAllCoursesFilterSelected
      : filter === librarySearchFilters.allPolicies
      ? isAllPoliciesFilterSelected
      : isMyCoursesFilterSelected;

  const courseFilters = reduce(
    availableFilters,
    (result, value) => {
      result[value] = memoizedFilterSelector(value);
      return result;
    },
    {}
  );

  const {
    data: courses,
    onStateChange,
    isFetching,
    paginationState,
    onApplyExternalFilters,
    refreshData
  } = useTableData(searchCourses, {
    externalFilters: {
      term: searchValue,
      ...courseFilters
    }
  });

  useEffect(() => {
    onApplyExternalFilters({
      term: searchValue,
      ...courseFilters
    });
  }, [searchValue, courseFilters]);

  const disableAddToMyCoursesButton = selectedRowIds.length === 0;

  const filterIsChecked = filter => {
    return filter && filter.checked;
  };

  /**
   * Contains the Search filters, add to my courses button, and the search results for the Library Search Page
   * Add to my courses button is disabled unless the user has checked at least one course in the search results
   */

  const handleClearFiltersAndSelectedRows = (filtersToo = true) => {
    const currentFilters = () =>
      reduce(
        availableFilters,
        (result, value) => {
          result[value] = { checked: memoizedFilterSelector(value) };
          return result;
        },
        {}
      );

    filtersToo
      ? setTableFilters(defaultFilters)
      : setTableFilters(currentFilters());
    history.push(librarySearch);
    setSearchValue("");
    setSelectedRowIds([]);
  };

  const handleSubmit = searchTerm => {
    history.push(librarySearch + "?search=" + searchValue);
    setSearchValue(searchTerm);
  };

  const hasFilters = filters => {
    return Object.values(filters).some(filterValue => filterValue.checked);
  };

  const showRowSelection = rowData => {
    return (
      !rowData.userAssignment ||
      !rowData.userAssignment.assignedTrainingObjectId
    ); // will return true for courses the user is not already assigned to
  };

  const onActionClick = rowData => {
    if (
      !rowData.userAssignment ||
      !rowData.userAssignment.assignedTrainingObjectId
    ) {
      addToCourses(rowData.id);
    } else {
      onStartOrResumeClick(rowData); // TODO: implement start/resume course
    }
    refreshData();
  };

  const getTableProps = filters => {
    if (filters) {
      const columns = [imageColumn, courseTypeColumn(t), titleColumn(t)];
      if (hasPolicyPartnerLearnerAccess) {
        columns.push(policyNumberColumn(t));
        columns.push(manualNameColumn(t, isIframed));
      }
      columns.push(iltDateColumn(t));
      columns.push(iltLocationColumn(t));
      if (
        !hasFilters(filters) ||
        filterIsChecked(filters[librarySearchFilters.myCourses])
      ) {
        columns.push(courseProgressColumn);
      }
      columns.push(courseActionColumn(onActionClick));
      return columns;
    } else {
      return null;
    }
  };

  const columns = useMemo(() => getTableProps(tableFilters), [tableFilters]);

  const addToCourses = ids => {
    onAddToCourses(ids).then(() => {
      refreshData();
      setSelectedRowIds([]);
    });
  };

  const coursesList = useSelector(({ userAssignmentsListState }) => {
    return courses && courses.length > 0
      ? selector(userAssignmentsListState, {
          type: "version",
          id: courses.map(({ id }) => id)
        })
      : [];
  });

  return (
    <SearchPageLayout
      title={
        isIframed
          ? t("learningCenterLibraryIframe.searchPlaceholder")
          : t("learningCenterLibrary.searchPlaceholder")
      }
      searchValue={searchValue}
      onSearchSubmit={handleSubmit}
      onSearchClear={() => handleClearFiltersAndSelectedRows(false)}
    >
      <Box>
        <SearchFilters
          onFiltersChange={setTableFilters}
          filters={tableFilters}
          onClearAllClick={handleClearFiltersAndSelectedRows}
        />
        <AddToMyCoursesButton
          disabled={disableAddToMyCoursesButton}
          onAddToCourses={() => addToCourses(selectedRowIds)}
        />
        <Table
          columns={columns}
          onStateChange={onStateChange}
          toggleSelection={isAllCoursesFilterSelected}
          showCheckbox={row => showRowSelection(row)}
          data={coursesList}
          loading={isFetching}
          paginationState={paginationState}
          paginated
          showRowSelection
          updateSelection={(rows, rowIds) => {
            setSelectedRowIds(rowIds ? rowIds.map(({ id }) => id) : []);
          }}
        />
      </Box>
    </SearchPageLayout>
  );
};

export default Search;

Search.propTypes = {
  filters: PropTypes.object,
  searchValue: PropTypes.string,
  onFiltersChange: PropTypes.func,
  onAddToCourses: PropTypes.func,
  onSelectedCoursesChange: PropTypes.func,
  selectedCourses: PropTypes.any,
  onSearchSubmit: PropTypes.func,
  onClearFilters: PropTypes.func,
  onStartOrResumeClick: PropTypes.func
};

Search.defaultProps = {
  initialSearchValue: ""
};
